import React, { useEffect, useState } from "react";

// Modules
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// App
import { apiEndpoints } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import { alertMessages } from "../../partials/alertMessages";
import Pagination from "../../partials/pagination";
import PageHeader from "../../partials/pageHeader";
import Error from "../../partials/error";
import NoData from "../../partials/noData";
import ListHeader from "../../partials/listHeader";
import ClientTheme from "../../core/clientTheme";
import { useWindowSize } from "../../partials/windowSize";
import SalesRoomItem from "./salesRoomItem";

// UI components
import Row from "react-bootstrap/Row";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import CompanyappLoader from "../../partials/companyappLoader";

const pageSize = 20;

// A fixed 12-unit grid at EVERY breakpoint — no "auto" columns. Content-
// sized columns take their width from each row's own text, so rows with
// long titles would drift out of line with the header (and overflow-wrap
// the last cell). Sums: xs 1+6+2+1+1+1, md 1+5+2+1+1+1+1,
// lg 1+4+1+1+2+1+1+1 — all 12. The header and salesRoomItem.js must
// stay column-for-column identical.
const listLabels = [
  { title: "ID", width: { xs: 1 }, className: "" },
  { title: "Name", width: { xs: 6, md: 5, lg: 4 } },
  {
    title: "Status",
    width: { xs: 2, lg: 1 },
    align: "center",
    colClass: "d-flex justify-content-center",
  },
  {
    title: "Visitors",
    width: { md: 1 },
    align: "center",
    colClass: "d-none d-md-flex justify-content-center",
  },
  {
    title: "Last activity",
    width: { lg: 2 },
    colClass: "d-none d-lg-block",
  },
  {
    title: "Share",
    width: { xs: 1 },
    align: "center",
    colClass: "d-flex justify-content-center",
  },
  {
    title: "Edit",
    width: { xs: 1 },
    align: "center",
    colClass: "d-flex justify-content-center",
  },
  {
    title: "Open",
    width: { xs: 1 },
    align: "center",
    colClass: "d-flex justify-content-center",
  },
];

export default function SalesRooms() {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [data, setData] = useState([]);
  const [gridView, setGridView] = useState(false);
  // "rooms" hides templates (they're org-wide and would clutter every
  // seller's list); "templates" shows only them for management.
  const [listMode, setListMode] = useState("rooms");

  const user = useSelector((state) => state).authReducer.user;
  const clientTheme = { ...ClientTheme() };
  const { width } = useWindowSize();
  const navigate = useNavigate();

  useEffect(() => {
    getContent(1);
    // eslint-disable-next-line
  }, []);

  const _setError = (_isError, _errorStatus, _errorMessage) => {
    setIsLoading(false);

    setIsError(_isError);
    setErrorStatus(_errorStatus);
    setErrorMessage(_errorMessage);
  };

  const getContent = (_currentPage) => {
    const path = `${apiEndpoints.salesRooms}?_format=json&items_per_page=${pageSize}&page=${
      _currentPage - 1
    }`;

    getOnlineNode(path, user.access_token)
      .then((response) => {
        setData(response.data.rows);
        setTotalItems(response.data.pager.total_items);
        setIsLoading(false);
      })
      .catch((_error) => {
        if (_error.response) {
          _setError(true, _error.response.status, _error.response.statusText);
        } else if (_error.request) {
          _setError(true, 0, alertMessages.requestError.message);
        } else {
          _setError(true, 0, alertMessages.unkownError.message);
        }
      });
  };

  const onPaginate = (_index) => {
    setIsLoading(true);

    setCurrentPage(_index);
    getContent(_index);
  };

  const renderPagination = () => {
    if (totalItems <= pageSize) {
      return null;
    } else {
      return (
        <Pagination
          owner="client"
          containerClasses={`creator-pagination d-flex justify-content-center align-items-center mt-4`}
          previousDisabled={currentPage === 1 ? true : false}
          nextDisabled={data.length < pageSize ? true : false}
          currentPage={currentPage}
          onPrevious={() => {
            onPaginate(currentPage - 1);
          }}
          onNext={() => {
            onPaginate(currentPage + 1);
          }}
        />
      );
    }
  };

  const dataComponent = () => {
    const visible = (data || []).filter((item) =>
      listMode === "templates" ? item.is_template : !item.is_template
    );

    if (visible.length > 0) {
      return (
        <Row className={`${!gridView ? "mx-0" : ""}`}>
          {visible.map((item) => {
            return (
              <React.Fragment key={`sales-room-${item.id}`}>
                <SalesRoomItem gridView={gridView} item={item} />
              </React.Fragment>
            );
          })}
        </Row>
      );
    } else {
      return <NoData activeIndex={0} />;
    }
  };

  return (
    <>
      {isError ? (
        <Error status={errorStatus} message={errorMessage} />
      ) : (
        <main className="sales-rooms screen">
          <PageHeader
            pageName="Sales Rooms"
            filters={false}
            owner="client"
            pageNameColor="clientHeadingScreens"
            svgLogo={clientTheme.pageLogos.svg}
            createNew
            contentTypeName="sales room"
            handleCreate={() => navigate("/sales-rooms/add")}
            viewToggle
            gridView={false}
            handleViewToggle={(value) => {
              setGridView(value);
            }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            className="mb-3"
            value={listMode}
            onChange={(e, value) => {
              if (value !== null) {
                setListMode(value);
              }
            }}
          >
            <ToggleButton value="rooms">Rooms</ToggleButton>
            <ToggleButton value="templates">Templates</ToggleButton>
          </ToggleButtonGroup>
          {isLoading ? (
            <CompanyappLoader />
          ) : (
            <>
              {!gridView && width > 576 && (
                <ListHeader
                  items={listLabels}
                  labelConfig={{
                    owner: "client",
                    variant: "subtitle1",
                    color: "clientPrimary.main",
                    weight: "medium",
                    lh: "wide",
                  }}
                />
              )}
              {dataComponent()}
              {renderPagination()}
            </>
          )}
        </main>
      )}
    </>
  );
}
